import Component from '@glimmer/component'
import { ThemeKeys } from 'teacher-ui-client/utils/themes'
import { eq } from 'ember-truth-helpers'

interface Signature {
  Element: HTMLInputElement
  Args: {
    outlineForced?: boolean
    themeKey?: ThemeKeys
    icon?: string
    iconStart?: boolean
    iconEnd?: boolean
  }
}

/**
 * A themed text input component.
 * Most "text" types should work (text, password, email, search, number, tel, ...).
 */
export class InputText extends Component<Signature> {
  get themeClass() {
    const { outlineForced, themeKey = ThemeKeys.default } = this.args

    /* Typically inputs only want their themed-outline applied when focused. In some cases, like for a validation error,
     * the outline classes should be applied even when not focused.
     *
     * Additionally, we can only transition smoothly using the outline colour (and not on outline width or style)
     *
     * This is why the logic is based on the colour, rather than just always setting the colour and instead switching
     * the outline width from 0 to 2.
     */
    const themes: Partial<Record<ThemeKeys, string>> = {
      [ThemeKeys.blue]: `focus:border-oceany-blue-400 ${
        outlineForced ? 'outline-oceany-blue-300/75' : 'outline-transparent'
      } focus:outline-oceany-blue-300/75`,
      [ThemeKeys.green]: `focus:border-ms-green-400 ${
        outlineForced ? 'outline-ms-green-300/75' : 'outline-transparent'
      } focus:outline-ms-green-300/75`,
      [ThemeKeys.darkBlue]: `focus:border-wl-blue-250 ${
        outlineForced ? 'outline-wl-blue-300/75' : 'outline-transparent'
      } focus:outline-wl-blue-300/75`,
      [ThemeKeys.orange]: `focus:border-juicy-orange-500 ${
        outlineForced ? 'outline-juicy-orange-300/75' : 'outline-transparent'
      } focus:outline-juicy-orange-300/75`,
      [ThemeKeys.default]: `focus:border-dusty-black-400 ${
        outlineForced ? 'outline-dusty-black-300/75' : 'outline-transparent'
      } focus:outline-dusty-black-300/75`,
    }
    return themes[themeKey] ?? themes.default
  }
  <template>
    <input
      type="text"
      class="border-dusty-black-100 placeholder:text-dusty-black-200 disabled:bg-dusty-black-50 rounded-md border bg-white p-2 outline outline-2 outline-offset-0 transition-all duration-200 disabled:cursor-not-allowed
        {{this.themeClass}}
        {{if (eq @icon 'search') 'input-text-search-icon bg-size-[1rem] bg-no-repeat bg-origin-content'}}
        {{if
          (eq @icon 'calendar-regular')
          'input-text-calendar-regular-icon bg-size-[1rem] bg-no-repeat bg-origin-content'
        }}
        {{if
          (eq @icon 'calendar-solid')
          'input-text-calendar-solid-icon bg-size-[1rem] bg-no-repeat bg-origin-content'
        }}
        {{if @iconStart 'bg-position-[left_-1.5rem_center] pl-8'}}
        {{if @iconEnd 'bg-position-[right_-1.5rem_center] pr-8'}}"
      ...attributes
    />
  </template>
}

export default InputText
